// Điểm mở rộng của module Nguồn hàng (FSD.md §2.1, §3.4): "Tạo mới · Dự án", Tìm nhanh "Căn / sản phẩm", lớp phủ
// projectForm, làm mới khi bảng hàng (booking.units) hoặc danh mục (inventory.projects) đổi.
import type { inventory } from '@cathouse/contracts';
import { http, qk, type RealtimeBinding } from '@/shared/api';
import { lazyComponent, UNIT_STATUS, fmtMoney, type OverlayDef } from '@/shared/lib';
import type { CreateItem, SearchProvider } from '@/widgets/app-shell';
import { INVENTORY_LIVE_KEYS } from '@/entities/unit';

/** Thành phần lớp phủ nạp khi mở (chunk riêng; OverlayHost nạp trước lúc rảnh). */
const ProjectFormOverlay = lazyComponent(() => import('./ui/overlays'), (x) => x.ProjectFormOverlay, 'ProjectFormOverlay');

/** Mục "Tạo mới" của module ở thanh trên (lọc theo module bật + quyền). */
export const createItems: CreateItem[] = [{ label: 'Dự án', icon: 'building', module: 'inventory', perm: 'inventory.edit', to: '.', overlay: ['projectForm', 'new'] }];

/** Tìm nhanh căn theo mã / địa chỉ ⇒ drawer căn của Chốt căn (module booking hiển thị) hoặc bảng giá của dự án. */
export const searchProviders: SearchProvider[] = [
  {
    id: 'units',
    module: 'inventory',
    group: 'Căn / sản phẩm',
    perm: ['inventory.view'],
    search: async (q, { signal, visible }) => {
      const hits = await http.get<inventory.UnitSearchHit[]>('/inventory/search', { query: { q, limit: 6 }, signal });
      const board = visible('booking');
      return hits.map((h) => ({
        key: h.unitId,
        icon: 'grid',
        title: h.code,
        sub: [h.projectName, h.type, UNIT_STATUS[h.effectiveStatus]?.label].filter(Boolean).join(' · '),
        meta: fmtMoney(h.price),
        ...(board ? { overlay: ['unit', h.unitId] as const } : { to: '/nguon-hang', search: { projectId: h.projectId, q: h.code } }),
      }));
    },
  },
];

/** ?projectForm=new|<projectId> — form dự án; thiếu inventory.edit ⇒ OverlayHost báo không có quyền. */
export const overlays: OverlayDef[] = [
  {
    key: 'projectForm',
    module: 'inventory',
    layer: 'modal',
    perm: 'inventory.edit',
    denied: { title: (v) => (v === 'new' ? 'Dự án mới' : 'Sửa dự án'), text: 'Bạn chưa có quyền sửa nguồn hàng' },
    component: ProjectFormOverlay,
  },
];

/** Trạng thái hiệu lực trên bảng giá + số đếm thẻ dự án (giờ cao điểm: gộp tin 1 s). */
export const realtime: RealtimeBinding[] = [
  { topic: 'booking.units', module: 'inventory', keys: () => INVENTORY_LIVE_KEYS, throttleMs: 1000 },
  // Đề xuất của agent (tạo / duyệt) cũng phát inventory.projects ⇒ làm mới hàng đợi duyệt
  { topic: 'inventory.projects', module: 'inventory', keys: () => [...INVENTORY_LIVE_KEYS, qk.agent.all] },
];
